import React, { useState, useCallback } from "react";
import { getSmoothStepPath, EdgeLabelRenderer, BaseEdge } from "@xyflow/react";
import { useNodeStorage } from "../../../stores/useNodeStorage";

const EDGE_COLOR = "#94a3b8";
const EDGE_COLOR_SELECTED = "#9a00ed";
const EDGE_WIDTH = 1.5;
const EDGE_WIDTH_HOVER = 2.5;

/**
 * SmartEdge - Custom edge for FlowBuilder V2 (@xyflow/react v12)
 *
 * Uses SmoothStep path for clean right-angle routing (avoids bezier loops).
 * Shows delete button on hover.
 */
export default function SmartEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
  data,
}) {
  const [hovered, setHovered] = useState(false);
  const storageItems = useNodeStorage();

  const isRunning = data?.running ?? false;
  const isActive = hovered || selected;

  // SmoothStep creates clean right-angle paths that never loop
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 8,
  });

  const handleDelete = useCallback(
    (evt) => {
      evt.stopPropagation();
      evt.preventDefault();
      storageItems.setNodesStorage(id);
      storageItems.setAct("deleteEdge");
    },
    [id, storageItems]
  );

  const color = isActive ? EDGE_COLOR_SELECTED : EDGE_COLOR;

  const computedStyle = {
    stroke: color,
    strokeWidth: isActive ? EDGE_WIDTH_HOVER : EDGE_WIDTH,
    transition: "stroke 0.15s, stroke-width 0.15s",
    ...(isRunning
      ? {
          strokeDasharray: "6 3",
          animation: "smartEdgeDash 0.6s linear infinite",
        }
      : {}),
    ...style,
  };

  return (
    <>
      {isRunning && (
        <style>
          {`@keyframes smartEdgeDash {
              to { stroke-dashoffset: -18; }
            }`}
        </style>
      )}

      {/* Invisible wider path for easier hover detection */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ cursor: "pointer" }}
      />

      {/* Visible edge */}
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={computedStyle}
      />

      {/* Delete button at center */}
      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: "all",
            opacity: isActive ? 1 : 0,
            transition: "opacity 0.15s",
          }}
          className="nodrag nopan"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <button
            onClick={handleDelete}
            style={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              border: `1.5px solid ${EDGE_COLOR_SELECTED}`,
              backgroundColor: "#fff",
              color: EDGE_COLOR_SELECTED,
              fontSize: 11,
              fontWeight: 700,
              lineHeight: "18px",
              textAlign: "center",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            }}
            title="Remover conexao"
          >
            &times;
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
